import React from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import EsimAPI from 'api/faber/EsimAPI'
import MenuClose from 'assets/icons/MenuClose.png'
import { AxiosError } from 'axios'

interface ReplayAllocationDialogProps {
  faberToken: string
  handleReplayAllocationDialogClose: () => void
  dialogOpen: boolean
  allocationId: string | null
}

function ReplayAllocationDialog({
  faberToken,
  allocationId,
  handleReplayAllocationDialogClose,
  dialogOpen,
}: ReplayAllocationDialogProps) {
  const queryClient = useQueryClient()
  
  const { mutate, isLoading } = useMutation(
    EsimAPI.replayAllocation,
    {
      onSuccess: () => {
        toast.success('Allocation Replayed successfully')
        queryClient.refetchQueries({ queryKey: ['customer-allocation-packs'] });
      },
      onError: (error) => {
        let message = null;
        if (error instanceof AxiosError) {
          message = error.response?.data.error;
        }
        toast.error(message ?? 'Failed to reallocate pack')
      },
      onSettled: () => {
        handleReplayAllocationDialogClose()
      },
    }
  )

  const handleReplayAllocation = () => {
    if (allocationId) {
      mutate({ allocationId, faberToken })
    } else {
      toast.error('No allocation selected')
    }
  }

  return (
    <Dialog
      open={dialogOpen}
      maxWidth="sm"
      fullWidth={true}
      onClose={handleReplayAllocationDialogClose}
      sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
    >
      <DialogTitle
        sx={{
          fontWeight: '700',
          fontSize: '20px',
          color: '#333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Replay Allocation pack to a user
        <IconButton
          onClick={handleReplayAllocationDialogClose}
          size="small"
          sx={{
            marginRight: '-8px',
            marginTop: '-8px',
          }}
        >
          <img
            style={{
              width: '20px',
              height: '20px',
            }}
            src={MenuClose}
            alt="Close"
          />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            marginTop: 2,
            marginBottom: 2,
            color: '#555',
          }}
        >
          Are you sure you want to replay this pack allocation?
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          padding: '16px',
          display: 'flex',
        }}
      >
        <LoadingButton variant='text' onClick={handleReplayAllocationDialogClose}>
          Cancel
        </LoadingButton>

        <LoadingButton
          variant="contained"
          onClick={handleReplayAllocation}
          loading={isLoading}
          loadingIndicator={<CircularProgress size={24} />}
          sx={{
            paddingX: 4,
            fontWeight: 'bold',
          }}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default ReplayAllocationDialog